import BaseScene from "../scenes/BaseScene";
import { ButtonStyleConfig } from "./LabelButton";

export enum IconButtonType {
  HOME = "HOME",
  SOUND = "SOUND",
  MUSIC = "MUSIC",
  PAUSE = "PAUSE",
}

// todo we can optimise this with making the off optional, and use that to define if the button is a toggle
const iconRefence: {
  [key: string]: { on: string; off: string; isToggle: boolean };
} = {
  HOME: { on: "home.png", off: "home.png", isToggle: false },
  PAUSE: { on: "pause.png", off: "pause.png", isToggle: false },
  SOUND: { on: "volume-high.png", off: "volume-off.png", isToggle: true },
  MUSIC: {
    on: "musical-notes.png",
    off: "musical-notes-off.png",
    isToggle: true,
  },
};

export default class IconButton extends Phaser.GameObjects.Container {
  /**
   * if input events are enabled.
   *
   * @type {boolean}
   * @memberof LabelButton
   */
  public enabled: boolean = true;

  private _iconModel: { on: string; off: string; isToggle: boolean };
  private _isToggle: boolean = false;
  public toggle: boolean = true;

  /**
   * contains style properties of the button
   *
   * @private
   * @type {*}
   * @memberof LabelButton
   */
  private _style: ButtonStyleConfig;

  /**
   * internal callback pointer
   *
   * @private
   * @memberof LabelButton
   */
  private _callback: (toggleState?: boolean) => void;

  /**
   *the background game object (currently image)
   *
   * @private
   * @type {Phaser.GameObjects.Image}
   * @memberof LabelButton
   */
  private _background: Phaser.GameObjects.Image;

  /**
   * the label game object (Phaser Text)
   *
   * @private
   * @type {Phaser.GameObjects.Text}
   * @memberof LabelButton
   */
  private _label: Phaser.GameObjects.Image;

  constructor(
    scene: BaseScene,
    x: number,
    y: number,
    callback: (toggleState?: boolean) => void,
    icon: IconButtonType,
    hoverCallback?: (toggleState?: boolean) => void
  ) {
    // super to creat the container at the specified co-ordinates.
    super(scene, x, y);

    // !important - as we are using the 'new' keyword (and not using the scene
    // factory methods, `scene.add` ) we need to add it to the scene manually
    scene.add.existing(this);

    // setup basic style
    this._style = {
      width: 60,
      height: 60,
      radius: 30,
      colour: scene.sponge.data.model.colours.primary,
      colourHover: scene.sponge.data.model.colours.hover,
      colourDown: scene.sponge.data.model.colours.primary_darken,
      colourLabel: scene.sponge.data.model.colours.label,
    };

    // if we dont have a texture for this style button lets create it.
    if (!scene.textures.exists("icon-button")) {
      const _backgroundGraphics = scene.make.graphics({ x: 0, y: 0 }, false);
      _backgroundGraphics.fillStyle(0xffffff, 1);
      _backgroundGraphics.fillRoundedRect(
        0,
        0,
        this._style.width,
        this._style.height,
        this._style.radius
      );
      _backgroundGraphics.generateTexture(
        "icon-button",
        this._style.width,
        this._style.height
      );
    }

    // back background and set inital colour;
    this._background = scene.add.image(0, 0, "icon-button");

    this._iconModel = iconRefence[icon];
    if (this._iconModel.isToggle) {
      this._isToggle = true;
    }
    this.colour = this._style.colour;

    // make label icon
    this._label = scene.add.image(0, 0, "icons", this._iconModel.on);
    this._label.setOrigin(0.5);
    this._label.setTint(this._style.colourLabel);

    // add to container
    this.add([this._background, this._label]);

    // set up events
    this._callback = callback;

    // as we are working with an grpahics object, we need to set it interaction with some geomerty to detect hit area.
    this._background.setInteractive();

    // on hover entry - we are using "fat arrow" functions "()=> {...", this essentially means
    // we dont need to worry about context, as this allways relates to the current class.
    this._background.on("pointerover", () => {
      if (!this.enabled) return;
      this.colour = this._style.colourHover;

      if (hoverCallback) {
        hoverCallback(this.toggle);
      }
    });

    // on hover exit
    this._background.on("pointerout", () => {
      if (!this.enabled) return;
      this.colour = this._style.colour;
    });

    // on down
    this._background.on("pointerdown", () => {
      if (!this.enabled) return;
      this.colour = this._style.colourDown;

      if (this._isToggle) {
        this.toggle = !this.toggle;
        this._label.setFrame(
          this.toggle ? this._iconModel.on : this._iconModel.off
        );
        this._callback(this.toggle);
      } else {
        this._callback();
      }
    });

    // on up
    this._background.on("pointerup", () => {
      if (!this.enabled) return;
      this.colour = this._style.colour;
    });
  }

  /**
   * sets the backgorund colour of the button
   *
   * @memberof LabelButton
   */
  set colour(color: number) {
    this._background.tint = color;
  }
}
